import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import Label from 'mrf/layout/Label';
import Tooltip from './Tooltip';
import { FormContext } from './FormCRUD';
import { useFormErrorSelector, useFormDataSelector } from 'mrf/redux/ReduxHelper';
import capitalize from '../utils/capitalize';
import ErrorTip from './ErrorTip';
import IF from './IF';
import T from 'mrf/utils/T';

FormFieldWrapper.propTypes = {
    context: PropTypes.object,

    label: PropTypes.string,
    field: PropTypes.string.isRequired, // Field name.
    ownedBy: PropTypes.string, // For owned entities, like Address
    tooltip: PropTypes.string,
    tooltipPlacement: PropTypes.string,
    required: PropTypes.bool, // Whether this field is required or not
    alwaysUndefinedValue: PropTypes.bool, // If this is enabled, the Component's value will always be ''
    disableTranslation: PropTypes.bool, // Determines if the label will be translated or not, defaults to False
    actions: PropTypes.any,
    children: PropTypes.any,
    disableErrors: PropTypes.bool, // Determines if errors will be rendered, defaults to False
}

export default function FormFieldWrapper({
    context,
    label,
    field,
    ownedBy,
    tooltip = '',
    tooltipPlacement,
    required,
    alwaysUndefinedValue,
    actions,
    span,
    disabled,
    disableTranslation,
    children,
    className,
    disableErrors = false,
}) {
    const formContext = useContext(FormContext);
    if (!context) {
        context = formContext;
    }

    let data = useFormDataSelector(context, field);
    if (alwaysUndefinedValue) {
        data = undefined;
    }

    const fieldData = data?.[field];

    // Generate the string: "OwnedBy.Field" where "OwnedBy." is only present if ownedBy is trueish.
    const errorKey = useMemo(
        () => [ownedBy, field].filter(Boolean).map(capitalize).join('.'),
        [ownedBy, field]
    );

    const readOnly = context?.readOnly;

    const errors = useFormErrorSelector(context, capitalize(field), fieldData !== undefined);

    const errorsHtml = useMemo(
        () => errors?.map?.((error, idx) => {
            if (typeof error === 'string' && !disableErrors) {
                return <ErrorTip key={idx} message={label ? error?.replace(errorKey, label) : error} />
            } else {
                return false;
            }
        }),
        [errors, errorKey, label, disableErrors]
    );

    return (
        <div className={className}>
            <IF condition={!!label}>
                <Label
                    required={required}
                    actions={actions}
                    readOnly={readOnly}
                    disableTranslation={disableTranslation}
                >
                    {label}
                </Label>
            </IF>
            <Tooltip
                content={tooltip}
                placement={tooltipPlacement}
                disabled={!tooltip}
                disableTranslation={disableTranslation}
            >
                {children}
            </Tooltip>
            <IF condition={!!span}>
                <span className={disabled ? 'cursor-text' : 'cursor-pointer'}>
                    <T>{span}</T>
                </span>
            </IF>
            {errorsHtml}
        </div>
    );
}
